<script setup lang="ts">
import type { Options, SortableEvent } from "sortablejs";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import { objectiveActions } from "@/action/objectiveActions";
import { objectiveMarkedAs } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import SidePanel from "@/components/SidePanel/SidePanel.vue";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import AddObjectiveCard from "@/components/objectives/AddObjectiveCard.vue";
import ObjectiveCard from "@/components/objectives/ObjectiveCard.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useSortable } from "@/composables/useSortable";
import { boardTypeToLevel } from "@/model/baseTypes";
import type { Objective } from "@/model/objective";
import { useArtStore } from "@/store/art";
import { useArtObjectivesPanelStore } from "@/store/artObjectivesPanel";
import { useBoardsStore } from "@/store/boards";
import { useUserStore } from "@/store/user";
import { eventTarget } from "@/utils/dom/dom";
import { isStatusDistributionFromGraph } from "@/utils/env/feature";

const DRAG_GROUP = "art-objectives-panel";

const store = useArtObjectivesPanelStore();
const artName = computed(() => useArtStore().current.name);
const programBoard = computed(() => useBoardsStore().boardByType("program"));
const objectives = computed(() => programBoard.value.objectives);
const stretchObjectives = computed(() => programBoard.value.stretchObjectives);
const readonly = computed(() => !useUserStore().isAllowed("edit"));
const showStatusDistribution = computed(() => isStatusDistributionFromGraph(useRoute()));

const addingCommitted = ref(false);
const isDragging = ref(false);

const committedListRef = useTemplateRef<HTMLElement>("committedList");
const stretchListRef = useTemplateRef<HTMLElement>("stretchList");

function sortableOptions(fromSection: "committed" | "stretch"): Options {
  return {
    group: DRAG_GROUP,
    animation: 100,
    ghostClass: "objective-drag-state",
    disabled: readonly.value,
    // The panel's scroll container (SidePanelContent) is a nested ancestor,
    // not the immediate sortable parent — browsers' native autoscroll doesn't
    // reach it reliably, so use SortableJS's own scroll loop.
    forceAutoScrollFallback: true,
    scrollSensitivity: 80,
    scrollSpeed: 20,
    onStart: () => (isDragging.value = true),
    onEnd: (evt) => onDragEnd(evt, fromSection),
  };
}

useSortable(committedListRef, sortableOptions("committed"));
useSortable(stretchListRef, sortableOptions("stretch"));

function startAddCommitted() {
  addingCommitted.value = true;
}

function cancelAddCommitted() {
  addingCommitted.value = false;
}

async function focusOnObjective(objective: Objective) {
  addingCommitted.value = false;
  await nextTick();
  document.getElementById(`objective-card-${objective.id}`)?.focus();
}

// The whole card is a click target for pinning, but clicks on buttons,
// menu triggers, "See more" toggles, and text fields inside the card must
// not also toggle the pin.
function onObjectiveRowClick(event: MouseEvent, objectiveId: string) {
  if (!eventTarget(event).closest("button, [role='button'], textarea, input, a")) {
    store.togglePin(objectiveId);
  }
}

function onDragEnd(evt: SortableEvent, fromSection: "committed" | "stretch") {
  isDragging.value = false;

  const objectiveId = evt.item.dataset.id;
  if (!objectiveId) return;

  // Determine destination section by checking which list the item ended up in.
  // SortableJS guarantees evt.to is the DOM container that received the drop.
  const stretch = evt.to === stretchListRef.value;
  const rank = evt.newIndex ?? 0;

  objectiveActions.move("modal", programBoard.value.id, objectiveId, { stretch, rank });

  const wasStretch = fromSection === "stretch";
  if (wasStretch !== stretch) {
    trackEvent(objectiveMarkedAs(objectiveId, stretch, boardTypeToLevel(programBoard.value.type)));
  }
}
</script>

<template>
  <SidePanel
    id="art-objectives-panel"
    class="art-objectives-panel"
    :active="store.active"
    side="left"
    name="art-objectives"
    resizable
    @close="store.close($event)"
  >
    <SidePanelContent :title="artName">
      <template v-if="!readonly" #header-actions>
        <IconButton
          icon="system/plus"
          :aria-label="$t('label.objective.add')"
          @click="startAddCommitted"
        />
      </template>

      <div class="panel-body">
        <div class="section-header">
          <span class="section-title">{{ $t("objectives.committed") }}</span>
          <span class="section-count">{{ objectives.length }}</span>
        </div>
        <AddObjectiveCard
          v-if="addingCommitted"
          type="committed"
          :board="programBoard"
          @cancel="cancelAddCommitted"
          @added="focusOnObjective"
        />
        <div ref="committedList" class="sortable-section">
          <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
          <ObjectiveCard
            v-for="(objective, index) in objectives"
            :key="objective.id"
            :objective
            type="committed"
            :index="index + 1"
            :board="programBoard"
            :is-dragging
            :expand-descriptions="false"
            :has-status-distribution="showStatusDistribution"
            :can-move-up="index > 0"
            :can-move-down="index < objectives.length - 1"
            :pinned="store.pinnedArtObjectiveId === objective.id"
            enable-link-drag
            clickable
            @click="onObjectiveRowClick($event, objective.id)"
          />
        </div>

        <div class="section-header">
          <span class="section-title">{{ $t("objectives.uncommitted") }}</span>
          <span class="section-count">{{ stretchObjectives.length }}</span>
        </div>
        <div ref="stretchList" class="sortable-section">
          <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
          <ObjectiveCard
            v-for="(objective, index) in stretchObjectives"
            :key="objective.id"
            :objective
            type="uncommitted"
            :index="index + 1"
            :board="programBoard"
            :is-dragging
            :expand-descriptions="false"
            :has-status-distribution="showStatusDistribution"
            :can-move-up="index > 0"
            :can-move-down="index < stretchObjectives.length - 1"
            :pinned="store.pinnedArtObjectiveId === objective.id"
            enable-link-drag
            clickable
            @click="onObjectiveRowClick($event, objective.id)"
          />
        </div>
      </div>
    </SidePanelContent>
  </SidePanel>
</template>

<style lang="scss" scoped>
@use "@/styles/z-index";
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

.art-objectives-panel {
  z-index: z-index.$menu;
}

.panel-body {
  padding: 0 16px 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  padding: 20px 0 8px;

  .section-title,
  .section-count {
    @include typography.small("semi-bold");
  }

  .section-count {
    color: colors.$text-subtle;
  }
}

.sortable-section {
  min-height: 40px;
}

.objective-drag-state {
  background-color: colors.$alpha-5;
  cursor: grabbing;
}
</style>
